import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAthleteAuth } from '@/contexts/AthleteAuthContext';
import { useThemeImages } from '@/hooks/useThemeImages';

export default function AthleteLoginPage() {
  const { login, athlete, mustChangePassword } = useAthleteAuth();
  const themeImages = useThemeImages();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect
  if (athlete && !mustChangePassword) {
    navigate('/athlete/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('請填寫帳號與密碼 / Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      await login(username.trim(), password);
      // Check if must change password (context will update)
      // We need a small delay to let state propagate
      toast.success('登入成功 / Login successful');
      // Navigation happens via useEffect or context state
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            '登入失敗 / Login failed')
          : '登入失敗 / Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // After login, redirect based on state
  if (mustChangePassword) {
    navigate('/athlete/change-password', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back to home link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>回到首頁 / Back to home</span>
        </Link>

        {/* Login Card */}
        <div className="bg-[var(--bg-card)] rounded-xl shadow-lg border border-[var(--border)] p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img src={themeImages.logo} alt="Logo" className="w-16 h-16 mx-auto mb-4 rounded-full object-cover bg-[var(--primary)]" />
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">選手登入</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Athlete Login</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
              >
                帳號 <span className="text-xs text-[var(--text-secondary)]">/ Username</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow"
                  placeholder="請輸入帳號"
                  autoComplete="username"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[var(--text-primary)] mb-1.5"
              >
                密碼 <span className="text-xs text-[var(--text-secondary)]">/ Password</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent transition-shadow"
                  placeholder="請輸入密碼"
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-[var(--primary)] text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>登入中... / Logging in...</span>
                </>
              ) : (
                <span>登入 / Login</span>
              )}
            </button>
          </form>

          {/* Help text */}
          <p className="mt-6 text-center text-xs text-[var(--text-secondary)]">
            帳號由鄉區工作人員提供，如有問題請洽服務台
            <br />
            Account provided by township staff. Contact help desk for assistance.
          </p>
        </div>

        {/* Event info */}
        <p className="mt-6 text-center text-xs text-[var(--text-secondary)]">
          第14屆全國泰雅族運動會暨第3屆新竹縣原住民族傳統技藝競賽
          <br />
          14th National Atayal Games & 3rd Hsinchu County Indigenous Games
        </p>
      </div>
    </div>
  );
}
